/**
 * API Client for Kandidatportal
 *
 * All endpoints use cookie-based authentication (candidate_session)
 */

const API_BASE = '/api';

/**
 * Check if running in browser environment
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Handle session expiration - redirect to login with message
 */
export function handleSessionExpired(): never {
  if (isBrowser()) {
    // Redirect to home with session expired message
    window.location.href = '/?error=session_expired&message=' +
      encodeURIComponent('Økten din har utløpt. Vennligst logg inn på nytt.');
  }
  throw new Error('Session expired');
}

/**
 * Check if a fetch response indicates session expiration
 * Use this for direct fetch calls that don't use fetchAPI
 */
export async function checkSessionExpired(res: Response): Promise<void> {
  if (res.status === 401) {
    const error = await res.json().catch(() => ({ error: 'Unauthorized' }));
    if (error.error === 'Session expired' ||
        error.error === 'No session' ||
        error.error === 'Authentication required' ||
        error.error === 'Unauthorized') {
      handleSessionExpired();
    }
    throw new Error(error.error || 'Ikke autorisert');
  }
}

async function fetchAPI(endpoint: string, options?: RequestInit) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  // Handle session expiration (401 Unauthorized)
  if (res.status === 401) {
    const error = await res.json().catch(() => ({ error: 'Unauthorized' }));
    // Check if it's a session expiration error
    if (error.error === 'Session expired' ||
        error.error === 'No session' ||
        error.error === 'Authentication required' ||
        error.error === 'Unauthorized') {
      handleSessionExpired();
    }
    throw new Error(error.error || 'Ikke autorisert');
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || res.statusText);
  }

  return res.json();
}

// ============================================================================
// Authentication
// ============================================================================

export async function getSession() {
  return fetchAPI('/candidate/auth/session');
}

export async function logout() {
  return fetchAPI('/candidate/auth/logout', { method: 'POST' });
}

export async function initiateVippsLogin(state: string, claimToken?: string) {
  return fetchAPI('/candidate/auth/vipps/login', {
    method: 'POST',
    body: JSON.stringify({ state, claim_token: claimToken }),
  });
}

// ============================================================================
// Claims
// ============================================================================

export async function getPendingClaim(claimToken: string) {
  return fetchAPI(`/exam/claim/${claimToken}`);
}

export async function getClaimStatus(examSessionId: string) {
  return fetchAPI(`/exam/${examSessionId}/claim-status`);
}

// ============================================================================
// Certificates
// ============================================================================

export async function getMyCertificates() {
  return fetchAPI('/candidate/certificates');
}

export async function getCertificateById(id: string) {
  return fetchAPI(`/certificates/${id}`);
}

export async function getCertificateByVerificationCode(code: string) {
  return fetchAPI(`/certificates/verify/${code}`);
}

export async function downloadCertificatePDF(id: string): Promise<Blob> {
  // Download signed European Digital Credential (Kompetanseattest)
  const res = await fetch(`${API_BASE}/candidate/certificates/${id}/download`, {
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error('Failed to download certificate');
  }

  return res.blob();
}

export async function toggleCertificateSharing(id: string, enabled: boolean) {
  return fetchAPI(`/certificates/${id}/share`, {
    method: 'PATCH',
    body: JSON.stringify({ share_enabled: enabled }),
  });
}

// ============================================================================
// Profile & Settings
// ============================================================================

export async function getMyProfile() {
  return fetchAPI('/candidate/profile');
}

export async function updateLanguage(language: 'no' | 'en') {
  return fetchAPI('/candidate/profile/language', {
    method: 'PATCH',
    body: JSON.stringify({ language }),
  });
}

export async function updateNotificationPreferences(preferences: {
  email?: boolean;
  sms?: boolean;
  verification_alerts?: boolean;
}) {
  return fetchAPI('/candidate/profile/notifications', {
    method: 'PATCH',
    body: JSON.stringify({ notification_preferences: preferences }),
  });
}

export async function requestAccountDeletion() {
  return fetchAPI('/candidate/profile/delete', {
    method: 'POST',
  });
}

export async function cancelAccountDeletion() {
  return fetchAPI('/candidate/profile/cancel-deletion', {
    method: 'POST',
  });
}

export async function exportMyData() {
  return fetchAPI('/candidate/profile/export');
}

// ============================================================================
// Helper Functions
// ============================================================================

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function formatDate(date: string | Date, locale: string = 'no-NO'): string {
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatDateTime(date: string | Date, locale: string = 'no-NO'): string {
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
